import { Link, useNavigate } from 'react-router-dom';
import type { MixedFeedItem } from '../pages/HomePage';
import { SLOVAKIA_KRAJE, SLOVAKIA_KRAJE_VIEWBOX } from '../data/slovakia-kraje';

interface RegionCount {
  region: string;
  count: number;
}

// Predtým fillOpacity jedného odtieňa (0.3–0.9) — pri reálnych dátach, kde sa
// kraje líšia rádovo 2-4×, to vyzeralo takmer jednoliato (user feedback
// 2026-08-02: "tie kraje sú všetky rovnaké... vyzerá to odporne"). Skutočná
// farebná interpolácia medzi dvoma farbami dá oveľa výraznejší rozptyl bez
// ohľadu na to, aký úzky je rozsah dát.
function lerpColor(from: string, to: string, t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  const [r1, g1, b1] = [1, 3, 5].map((i) => parseInt(from.slice(i, i + 2), 16));
  const [r2, g2, b2] = [1, 3, 5].map((i) => parseInt(to.slice(i, i + 2), 16));
  const r = Math.round(r1! + (r2! - r1!) * clamped);
  const g = Math.round(g1! + (g2! - g1!) * clamped);
  const b = Math.round(b1! + (b2! - b1!) * clamped);
  return `rgb(${r}, ${g}, ${b})`;
}

// Trojfarebný prechod namiesto jedného odtieňa (user feedback 2026-08-02:
// "nemusí byť iba do zelena ladená, to mi tam najviac vadí") — navy → morská
// zelená → jantárová, skutočná zmena farebnej rodiny naprieč poradím, nie
// len sýtosť/svetlosť jednej farby.
function rankColor(t: number): string {
  const clamped = Math.max(0, Math.min(1, t));
  if (clamped < 0.5) return lerpColor('#111a4a', '#167e6c', clamped / 0.5);
  return lerpColor('#167e6c', '#e0a458', (clamped - 0.5) / 0.5);
}

const REGION_ABBR: Record<string, string> = {
  'Prešovský kraj': 'PO',
  'Žilinský kraj': 'ZA',
  'Nitriansky kraj': 'NR',
  'Trnavský kraj': 'TT',
  'Banskobystrický kraj': 'BB',
  'Trenčiansky kraj': 'TN',
  'Bratislavský kraj': 'BA',
  'Košický kraj': 'KE',
};

function kindClass(kind: MixedFeedItem['kind']): string {
  if (kind === 'auction') return 'k-drazba';
  if (kind === 'crz') return 'k-zmluva';
  return 'k-insolv';
}

// "Terminál" — 3. revízia sekcií Dnes v registroch / Pokrytie podľa kraja /
// Ďalšie sekcie (user request 2026-07-19: úplne nový vizuál, "nech to zaujme",
// vybraný z 2 navrhnutých artefaktov — Register vs. Terminál). Tmavý dátový
// dashboard panel vložený do inak svetlej stránky (podobne ako hero-band so
// sieťou) — zámerný kontrast, nie chyba zjednotenia témy. Celkový počet firiem
// je zámerne zaokrúhlený (formatApprox v HomePage.tsx), nie exaktné číslo —
// jednotlivé kraje v spektre ostávajú presné, keďže je to skôr dátová tabuľka.
export function TerminalRegistry({
  mixedFeed,
  regionCounts,
  totalCompaniesApprox,
}: {
  mixedFeed: MixedFeedItem[];
  regionCounts: RegionCount[] | undefined;
  totalCompaniesApprox: string;
}): React.JSX.Element {
  const navigate = useNavigate();
  const sorted = [...(regionCounts ?? [])].sort((a, b) => b.count - a.count);
  const max = Math.max(1, ...sorted.map((r) => r.count));
  const topRegion = sorted[0]?.region;
  const countByRegion = new Map(sorted.map((r) => [r.region, r.count]));
  // Farba podľa POORADIA, nie podľa absolútnej hodnoty (user feedback
  // 2026-08-02: "je to iba celé zelené... vôbec neviem čo mi to má
  // povedať") — pri reálnych dátach sú kraje príliš blízko na to, aby
  // magnitude-based škála (aj s odmocninou) dala viditeľný rozdiel. Rank
  // garantuje 8 zreteľne odlíšených krokov bez ohľadu na to, aké tesné sú
  // si reálne počty.
  const rankByRegion = new Map(sorted.map((r, i) => [r.region, i]));
  const goToRegion = (region: string) =>
    navigate(`/vyhladavanie?region=${encodeURIComponent(region)}`);

  return (
    <section className="terminal-block">
      <div className="ambient" aria-hidden="true" />
      <div className="stage">
        <span className="section-eyebrow">Živé dáta</span>
        <h2 className="tb-heading">Posledné zápisy v registroch a firmy podľa kraja</h2>
        <div className="grid2">
          <div>
            <h3 className="tb-title">Posledné zápisy</h3>
            <p className="section-sub">Obchodný vestník · dražby · centrálny register zmlúv</p>
            <div className="panel">
              {mixedFeed.map((item, i) => (
                <div className="t-row" key={item.id}>
                  <div className="t-date">{new Date(item.date).toLocaleDateString('sk-SK')}</div>
                  <div className={`t-kind ${kindClass(item.kind)}`}>{item.pillLabel}</div>
                  <div className="t-content">
                    <div className="t-title">
                      <Link to={item.linkTo}>{item.title}</Link>
                      {i === 0 && (
                        <span className="new-flag">
                          <i />
                          NOVÉ
                        </span>
                      )}
                    </div>
                    {item.subtitle && <div className="t-meta">{item.subtitle}</div>}
                  </div>
                </div>
              ))}
              <div className="panel-links">
                <Link to="/insolvencie">Insolvencie</Link>
                <Link to="/drazby">Dražby</Link>
                <Link to="/zmluvy">Zmluvy</Link>
              </div>
            </div>
          </div>

          <div>
            <h3 className="tb-title">Firmy podľa kraja</h3>
            <p className="section-sub">Klikni na kraj a pozri si firmy v ňom</p>
            <div className="panel">
              <div className="signal-total-row">
                <div className="signal-total">{totalCompaniesApprox}</div>
              </div>
              <div className="signal-total-label">firiem a živnostníkov spolu</div>

              <div className="kraje-map-wrap">
                <svg
                  className="kraje-map"
                  viewBox={SLOVAKIA_KRAJE_VIEWBOX}
                  role="img"
                  aria-label="Mapa Slovenska podľa počtu firiem v kraji"
                >
                  {Object.entries(SLOVAKIA_KRAJE).map(([region, d], i) => {
                    const count = countByRegion.get(region) ?? 0;
                    const rank = rankByRegion.get(region) ?? sorted.length - 1;
                    const rankT = sorted.length > 1 ? rank / (sorted.length - 1) : 0;
                    return (
                      <path
                        key={region}
                        d={d}
                        className={`kraj-shape${region === topRegion ? ' kraj-shape--top' : ''}`}
                        style={{
                          fill: rankColor(rankT),
                          animationDelay: `${i * 0.06}s`,
                        }}
                        tabIndex={0}
                        role="link"
                        aria-label={`${region}: ${count.toLocaleString('sk-SK')} firiem`}
                        onClick={() => goToRegion(region)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            goToRegion(region);
                          }
                        }}
                      >
                        <title>{`${region}: ${count.toLocaleString('sk-SK')} firiem`}</title>
                      </path>
                    );
                  })}
                </svg>
              </div>

              <div className="spectrum">
                {sorted.map((r, i) => {
                  const rankT = sorted.length > 1 ? i / (sorted.length - 1) : 0;
                  return (
                    <Link
                      key={r.region}
                      to={`/vyhladavanie?region=${encodeURIComponent(r.region)}`}
                      className={`bar${i === 0 ? ' top' : ''}`}
                      style={{ animationDelay: `${0.4 + i * 0.05}s` }}
                    >
                      <span className="count">{r.count.toLocaleString('sk-SK')}</span>
                      <div
                        className="col"
                        style={{ height: `${(r.count / max) * 100}%`, background: rankColor(rankT) }}
                      />
                    </Link>
                  );
                })}
              </div>
              <div className="spectrum-labels">
                {sorted.map((r) => (
                  <span key={r.region}>{REGION_ABBR[r.region] ?? r.region.slice(0, 2)}</span>
                ))}
              </div>
              <p className="panel-caption">
                Od Bratislavy po Košice — klikni na ktorýkoľvek kraj a hneď uvidíš, ktoré firmy
                tam sídlia a ako si vedú.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
